import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { AppDispatch } from '../../store';
import { authenticateUserFromStorage } from '../sessions/sessionSlice';
import UserProfileButton from './UserProfileButton';
import SearchBar from './SeachBar';
import * as REQUEST_REQUIREMENTS from '../../api/requestRequirements';
import SideBar from '../../components/sideBar/SideBar';
import Notifications from './Notifications';
import { MessagesToolbar } from '../../components/toolbar/messages/MessagesToolbar';
import socketConn from '../../../socket';
import useSocketSetup from './useSocketSetup';
import { getUserGenericConfig } from '../sessions/sessionConfig/sessionUserConfigurations';
import { AppBar, Box, IconButton, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import jinnerLogo from '../../assets/logo-white-name-white-without-background-portrait.png';

// — Design tokens —
const BG_APPBAR      = '#5c1a1a';
const TEXT_SECONDARY = 'rgba(244,244,246,0.70)';
const TEXT_PRIMARY   = '#f4f4f6';
const BORDER_BOTTOM  = '1px solid rgba(0,0,0,0.25)';

export default function ResponsiveAppBar() {
  const dropDownRef = useRef<HTMLDivElement | null>(null);
  const currentUser = useSelector((state: RootState) => state.session.currentUser);
  const authHeaders = useSelector((state: RootState) => state.session.authHeaders);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const userValidated = useSelector((state: RootState) => state.session.loggedIn);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [socket] = useState(() => socketConn(2));
  const [PRIVATE_ROUTES, setPrivateRoutes] = useState<REQUEST_REQUIREMENTS.EndPoints>();
  const [menuButtonClicked, setMenuButtonClicked] = useState(false);
  const [searchInputQuery, setSearchInputQuery] = useState<string>('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchClicked, setSearchClicked] = useState<boolean>(!isMobile);

  useSocketSetup(socket);

  useEffect(() => {
    const currentLocalization = location.pathname.split('/')[1];
    if (currentLocalization !== 'search') {
      setSearchInputQuery('');
    }
  }, [location]);

  useEffect(() => {
    const fetchUserMoreInfo = async () => {
      if (authHeaders && currentUser && currentUser.nickname) {
        try {
          await dispatch(
            getUserGenericConfig({
              authHeaders,
              userNickname: currentUser.nickname,
            })
          );
        } catch (error) {
          console.error('Error fetching user info:', error);
        }
      }
    };
    fetchUserMoreInfo();
  }, [currentUser]);

  useEffect(() => {
    async function authenticateUser() {
      if (!userValidated) {
        await dispatch(authenticateUserFromStorage());
      }
    }

    function handleClickOutside(event: MouseEvent) {
      if (dropDownRef.current && !dropDownRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }

    if (currentUser) {
      const routeParams = { userNickname: currentUser.nickname };
      const routes = REQUEST_REQUIREMENTS.handlePrivateRoutes({ ROUTE_PARAMS: routeParams });
      setPrivateRoutes(routes);
    }

    authenticateUser();
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (userValidated) {
      setIsMenuOpen(false);
    }
  }, []);

  const handleSignIn = () => {
    navigate('/auth/sign_in');
  };

  function handleGoHome() {
    if (PRIVATE_ROUTES && PRIVATE_ROUTES.USER) {
      navigate(PRIVATE_ROUTES.USER);
    } else {
      navigate('');
    }
  }

  const handleSideBarCloseOpen = (isClicked: boolean) => {
    setMenuButtonClicked(!isClicked);
  };

  const iconButtonSx = {
    color: TEXT_SECONDARY,
    '&:hover': { color: TEXT_PRIMARY, bgcolor: 'rgba(255,255,255,0.06)' },
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: BG_APPBAR,
          borderBottom: BORDER_BOTTOM,
          boxShadow: 'none',
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 64 },
            px: { xs: 1.5, sm: 2 },
            gap: 1,
          }}
        >
          {/* — Esquerda: menu burger + logo — */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {userValidated && (
              <IconButton
                size="small"
                onClick={() => handleSideBarCloseOpen(menuButtonClicked)}
                sx={iconButtonSx}
                aria-label="Abrir menu lateral"
              >
                <MenuIcon />
              </IconButton>
            )}

            <Box
              onClick={handleGoHome}
              sx={{ cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center' }}
            >
              <Box
                component="img"
                src={jinnerLogo}
                alt="Jinner"
                sx={{ height: { xs: 30, sm: 40 } }}
              />
            </Box>
          </Box>

          {/* — Centro: barra de pesquisa (flex-grow) — */}
          {userValidated && (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
              {isMobile && !searchClicked ? (
                <IconButton
                  size="small"
                  onClick={() => setSearchClicked(true)}
                  sx={iconButtonSx}
                  aria-label="Pesquisar"
                >
                  <SearchIcon />
                </IconButton>
              ) : (
                <SearchBar
                  searchInputQuery={searchInputQuery}
                  setSearchInputQuery={setSearchInputQuery}
                  setShowComponent={setSearchClicked}
                />
              )}
            </Box>
          )}

          {/* — Direita: mensagens, notificações, avatar — */}
          <Box
            ref={dropDownRef}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 'auto' }}
          >
            {userValidated && (!isMobile || !searchClicked) && (
              <>
                <MessagesToolbar />
                <Notifications />
              </>
            )}

            <UserProfileButton
              isMenuOpen={isMenuOpen}
              setIsMenuOpen={setIsMenuOpen}
              onSignIn={handleSignIn}
            />
          </Box>
        </Toolbar>
      </AppBar>

      {menuButtonClicked && userValidated && (
        <SideBar close={() => handleSideBarCloseOpen(menuButtonClicked)} />
      )}
    </>
  );
}
